/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React from 'react'

import type { EuiButtonProps } from '@elastic/eui'
import { EuiButton, EuiCallOut, EuiSpacer } from '@elastic/eui'

import useBillingDetails from '@modules/billing-lib/billingDetails/useBillingDetails'
import type { BillingDetails } from '@modules/billing-api/customTypes'
import { isMarketplaceCustomer } from '@modules/billing-lib/billingDetails/utils'
import { FormattedMessage } from '@modules/i18n'
import usePermissions from '@modules/permissions-lib/hooks'
import { useFlagsWhenLoaded } from '@modules/launchdarkly'
import { useConfig } from '@modules/cui/ConfigContext'

import { supportUrl } from '@/lib/urlBuilder'

import CreditCardModalButton from '../CreditCard/CreditCardModalButton'

import type { ReactNode } from 'react'

type Notification = Record<
  Extract<BillingDetails['billing_status'], 'terminated' | 'suspended' | 'pending_suspension'>,
  {
    title: ReactNode
    message: ReactNode
    color: EuiButtonProps['color']
    iconType: EuiButtonProps['iconType']
  }
>

const notifications: Notification = {
  pending_suspension: {
    title: (
      <FormattedMessage
        id='billing-status.pending-suspension.title'
        defaultMessage='Pending Suspension'
      />
    ),
    message: (
      <FormattedMessage
        id='billing-status.pending-suspension.message'
        defaultMessage='There is an outstanding balance on your Elastic Cloud Subscription. To keep your deployments active and to avoid your data being permanently deleted, please pay your outstanding balance.'
      />
    ),
    color: 'warning',
    iconType: 'warning',
  },
  suspended: {
    title: (
      <FormattedMessage
        id='billing-status.account-suspended.title'
        defaultMessage='Account Suspended'
      />
    ),
    message: (
      <FormattedMessage
        id='billing-status.account-suspended.message'
        defaultMessage='Your Elastic Cloud subscription is suspended due to an outstanding balance. To prevent deployment termination and permanent data loss, please pay your balance as soon as possible.'
      />
    ),
    color: 'warning',
    iconType: 'warning',
  },
  terminated: {
    title: (
      <FormattedMessage
        id='billing-status.account-terminated.title'
        defaultMessage='Account Terminated'
      />
    ),
    message: (
      <FormattedMessage
        id='billing-status.account-terminated.message'
        defaultMessage='Your Elastic Cloud subscription has been terminated due to non-payment. To restore your deployments, you must pay your outstanding balance within 30 days of the final email notice. After this period, your data will be permanently deleted.'
      />
    ),
    color: 'danger',
    iconType: 'error',
  },
}

const BillingStatus = () => {
  const { data } = useBillingDetails()
  const { hasPermissions } = usePermissions([{ type: 'billing-organization', action: 'create' }]) // Org/Billing Admin only
  const [_, flags] = useFlagsWhenLoaded()
  const isUserConsole = useConfig('APP_NAME') === 'userconsole'

  if (!data) {
    return null
  }

  const notification = notifications[data.billing_status]
  const isTerminated = data.billing_status === 'terminated'

  if (
    !notification ||
    isMarketplaceCustomer(data) ||
    (isUserConsole && !flags.billingStatus) // Hide billing status in User Console if the flag is not enabled
  ) {
    return null
  }

  return (
    <EuiCallOut
      title={notification.title}
      color={notification.color}
      iconType={notification.iconType}
      data-test-id={`billing-status_${data.billing_status}`}
    >
      {notification.message}
      <EuiSpacer size='m' />
      {isTerminated ? (
        <EuiButton
          element='button'
          fill={true}
          color='danger'
          href={supportUrl()}
          target='_blank'
          data-test-id='billing-status.contact-support-button'
        >
          <FormattedMessage id='billing-status.contact-button' defaultMessage='Contact support' />
        </EuiButton>
      ) : (
        <CreditCardModalButton
          type='full'
          disabled={!hasPermissions}
          tooltipMessage={
            !hasPermissions ? (
              <FormattedMessage
                id='billing-status.credit-card-button.permissions-message'
                defaultMessage='You are not authorized to perform this action. Contact your Organization Owner.'
              />
            ) : undefined
          }
          buttonProps={{ fill: true, color: notification.color }}
        >
          <FormattedMessage
            id='billing-status.update-payment-details-button'
            defaultMessage='Update payment details'
          />
        </CreditCardModalButton>
      )}
    </EuiCallOut>
  )
}

export default BillingStatus
